import type { MapCenter } from '../providers/HomePageProvider';
import { useResponsive } from '../providers/ResponsiveProvider';
import { arrayDiff } from '../utils';
import type { MapViewport } from './ArcgisMap';
import {
  FEED_COLOR,
  feedMarkerKind,
  type FeedMarkerKind,
} from './Feed/feedStatus';
import type { IFeedItem } from './FeedItem/FeedItem';
import GeoMap from './GeoMap';
import MapCenterPin from './MapCenterPin';
import MapSearch from './MapSearch/MapSearch';
import {
  Box,
  CircularProgress,
  Stack,
  useTheme,
} from '@presov.sk/presov-ui-react';
import React from 'react';

/**
 * Moduly, ktorými sa čiara k vybranej značke pozicuje a ktorými sa počíta cieľ
 * letu na detail. Sťahujú sa hneď pri načítaní mapy, nie až pri prvom kliknutí
 * na značku: dovtedy bolo prepojenie po prvom kliknutí pol sekundy nikde, lebo
 * čakalo na `import()`. Druhé kliknutie už bolo okamžité, čo je presne stopa
 * čakania na modul.
 */
const positioningModules = Promise.all([
  import('@arcgis/core/geometry/Point'),
  import('@arcgis/core/core/reactiveUtils'),
  import('@arcgis/core/geometry/support/webMercatorUtils'),
]).then(([{ default: Point }, { watch }, { lngLatToXY }]) => ({
  Point,
  watch,
  lngLatToXY,
}));

let positioning: Awaited<typeof positioningModules> | undefined;
positioningModules.then((modules) => {
  positioning = modules;
});

/**
 * Polomer zhlukovania v pixeloch. Musí sedieť s `clusterRadius` v definícii
 * vrstvy - podľa neho sa zoskupenie skladá aj rozoberá.
 *
 * Je väčší než najväčšie koliesko zoskupenia (48 px), a to zámerne. Zhlukovanie
 * ArcGISu je mriežka o hrane `clusterRadius` a koliesko sedí v **ťažisku**
 * svojich členov, takže pri hrane 60 px koliesko bunku takmer vyplnilo a dve
 * susedné zoskupenia si na mape sadali cez seba - z jedného potom nebolo vidno
 * ani číslo, lebo ArcGIS prekryté popisky vynecháva.
 */
const CLUSTER_RADIUS = 80;

/**
 * Od tohto priblíženia sa značky **nezhlukujú**.
 *
 * Nie je to kozmetika, je to obchádzka chyby v zhlukovaní ArcGISu. Po dvojici
 * letov „oddiaľ a prileť späť" (presne to, čo robí zatvorenie a otvorenie
 * detailu) zostane zhlukovaná vrstva rozladená: dáta v nej sú, na mape kreslí
 * značky na starých miestach a `hitTest` na nich **nenájde nič**, takže sa na
 * ne nedá kliknúť. Odmerané v prehliadači na `datahub-local`: so zhlukovaním
 * padli štyri behy zo štyroch **aj na verzii bez týchto zmien** (teda nejde
 * o regresiu), bez zhlukovania ani jeden. Nezaberá na to nič z toho, čo by
 * vrstvu malo prebrať - ani `refresh()`, ani nové `featureReduction`, ani
 * prepnutie `visible`, ani posun mapy; stav je trvalý.
 *
 * Prah je nad `DETAIL_ZOOM`, takže v priblížení, kde sa do značiek klikáva
 * a kde beží detail, žiadne zhlukovanie nie je. Zoskupenia zostávajú tam, kde
 * majú zmysel - v priblížení mesta, kde by sa značky prekrývali - aj s klikom
 * na zoskupenie, ktorý zúži zoznam v paneli.
 */
const CLUSTER_MAX_ZOOM = 16;

/**
 * Typy značiek feedu v poradí, v akom idú do koláča zoskupenia.
 *
 * Sú to **stavy podnetu** (a novinka), nie druhy položky: gulička na mape má
 * farbu toho, čo o podnete hovorí legenda pod mapou aj bunka štatistiky
 * v paneli, a tú farbu drží `FEED_COLOR` v `feedStatus.ts`. Vlastný podnet
 * medzi nimi **nie je** - „Moje" nie je stav, ale pohľad na zoznam, a modrá
 * gulička len preň by predstierala štvrtý stupeň semafora. A nie je tu ani
 * počet hlasov: ten kreslia prstence okolo značky (`VOTE_HALO_SIZES_PX`),
 * nie ďalšia farba.
 */
const FEED_KINDS = ['news', 'open', 'accepted', 'resolved'] as const;

/**
 * Farby značiek. Berú sa z témy cez `FEED_COLOR`, takže mapa si vlastnú sadu
 * farieb nedrží - sú to presne tie, ktoré má legenda pod ňou.
 *
 * Paleta je opísaná tvarom, nie typom témy: `ReturnType<typeof useTheme>` je
 * generické a mimo komponentu z neho vypadne `{}`.
 */
const feedKindColors = (
  palette: Record<(typeof FEED_COLOR)[FeedMarkerKind], { main: string }>,
): Record<FeedMarkerKind, string> => ({
  news: palette[FEED_COLOR.news].main,
  open: palette[FEED_COLOR.open].main,
  accepted: palette[FEED_COLOR.accepted].main,
  resolved: palette[FEED_COLOR.resolved].main,
});

/**
 * Veľkosť obyčajnej značky. Je to východisková veľkosť `simple-marker`u
 * (12 bodov), napísaná ale výslovne: tou istou hodnotou musí byť veľký aj
 * koláč o jednej značke, inak by osamotený podnet vyzeral ako zhluk.
 */
const MARKER_SIZE_PX = 16;
const MARKER_SIZE = `${MARKER_SIZE_PX}px`;

/**
 * Počet hlasov na značke: **prstence**, nie iná farba.
 *
 * Farba guličky patrí stavu podnetu (`FEED_KINDS`) a druhá vec, ktorú by tá
 * istá plocha hovorila, by ju prebila - odhlasovaný nový podnet bol žltý, teda
 * na mape na nerozoznanie od podnetu v riešení. Hlasy preto značku obopnú:
 * priemery prstencov od najmenšieho po najväčší a `VOTE_HALO_STOPS`, od koľkých
 * hlasov ktorý pribudne. Prstence sú **najviac tri**; zoznam určuje aj koľko
 * ich je, aj aké sú veľké, takže sa mení na jednom mieste.
 *
 * Hranice sú pevné, nie relatívne k najhlasovanejšiemu podnetu vo výreze:
 * relatívne by tomu istému podnetu pribúdali a ubúdali prstence pri každom
 * posune mapy.
 */
const VOTE_HALO_STOPS = [1, 5, 15];
const VOTE_HALO_SIZES_PX = [26, 36, 46];
/**
 * Krytie jedného prstenca. Prstence sú plné kotúče kreslené cez seba, takže sa
 * krytie skladá a fádnutie smerom von vyjde samo (22 % vonku, 39 % v strede,
 * 53 % pri guličke) - bez toho by tri rovnaké kotúče vyzerali ako jeden.
 */
const VOTE_HALO_ALPHA = 0.22;

/** Koľko prstencov patrí podnetu podľa počtu jeho hlasov. */
const voteHaloLevel = (votesCount = 0) =>
  VOTE_HALO_STOPS.filter((stop) => votesCount >= stop).length;

/** Polomer rámu značky v CIM. `size` symbolu naň mapuje priemer. */
const CIM_FRAME = 5;

/**
 * Vrcholy kruhu. CIM primitív na kruh nemá - kreslí sa ako polygón, a 32
 * vrcholov je na guľôčku pod 50 px viac než dosť.
 */
const cimCircleRing = () =>
  Array.from({ length: 33 }, (_, step) => {
    const angle = (step / 32) * Math.PI * 2;
    return [
      Math.round(Math.cos(angle) * CIM_FRAME * 1000) / 1000,
      Math.round(Math.sin(angle) * CIM_FRAME * 1000) / 1000,
    ];
  });

/** `#RRGGBB` na `[r, g, b]`; CIM chce farbu poľom, nie reťazcom. */
const hexToRgb = (hex: string): [number, number, number] => {
  const value = parseInt(hex.replace('#', ''), 16);
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
};

/**
 * Jeden kotúč symbolu. Veľkosť je v **pixeloch**, ako všetko ostatné v tomto
 * súbore; CIM ju chce v bodoch, tak sa prepočíta (1 bod = 4/3 px).
 */
const cimCircleLayer = (
  sizePx: number,
  fill: number[],
  outline?: { color: number[]; width: number },
) => ({
  type: 'CIMVectorMarker',
  enable: true,
  size: (sizePx * 3) / 4,
  colorLocked: true,
  anchorPointUnits: 'Relative',
  frame: {
    xmin: -CIM_FRAME,
    ymin: -CIM_FRAME,
    xmax: CIM_FRAME,
    ymax: CIM_FRAME,
  },
  markerGraphics: [
    {
      type: 'CIMMarkerGraphic',
      geometry: { rings: [cimCircleRing()] },
      symbol: {
        type: 'CIMPolygonSymbol',
        symbolLayers: [
          ...(outline
            ? [
                {
                  type: 'CIMSolidStroke',
                  enable: true,
                  width: outline.width,
                  color: outline.color,
                },
              ]
            : []),
          { type: 'CIMSolidFill', enable: true, color: fill },
        ],
      },
    },
  ],
  scaleSymbolsProportionally: true,
  respectFrame: true,
});

/**
 * Značka feedu: gulička vo farbe stavu. Prstence hlasov v nej **nie sú** -
 * kreslí ich vlastná vrstva pod feedom (`voteHaloSymbol`, `haloFeatureLayer`),
 * lebo v zhluku by ich značka nemala kde mať.
 *
 * Je to `CIMSymbol`, nie `simple-marker`, aby bola z jedného kusa so svojou
 * obrubou a aby ju bolo možné poskladať tou istou pomôckou ako prstence.
 *
 * Pretypovanie je na hranici typov ArcGISu zámerné: `CIMSymbolReference`
 * popisuje v typoch behové triedy, kým API sem berie obyčajný JSON.
 */
const feedMarkerSymbol = (color: string) =>
  ({
    type: 'cim',
    data: {
      type: 'CIMSymbolReference',
      symbol: {
        type: 'CIMPointSymbol',
        symbolLayers: [
          cimCircleLayer(MARKER_SIZE_PX, [...hexToRgb(color), 255], {
            color: [255, 255, 255, 255],
            width: 1,
          }),
        ],
      },
    },
  }) as unknown as __esri.CIMSymbolProperties & { type: 'cim' };

/**
 * Prstence hlasov, teda halo pod značkou. Sú to plné kotúče kreslené cez seba;
 * vrstvy sa v CIM kreslia odzadu, takže prvá je navrchu - poradie je preto od
 * najmenšieho po najväčší, rovnako ako keď prstence patrili priamo značke.
 *
 * Symbol nesie **len** prstence, žiadnu guličku: leží vo vlastnej vrstve pod
 * feedom, aby bol vidieť aj vtedy, keď značku nahradí koláč zhluku.
 */
const voteHaloSymbol = (color: string, level: number) =>
  ({
    type: 'cim',
    data: {
      type: 'CIMSymbolReference',
      symbol: {
        type: 'CIMPointSymbol',
        symbolLayers: VOTE_HALO_SIZES_PX.slice(0, level).map((sizePx) =>
          cimCircleLayer(sizePx, [
            ...hexToRgb(color),
            Math.round(VOTE_HALO_ALPHA * 255),
          ]),
        ),
      },
    },
  }) as unknown as __esri.CIMSymbolProperties & { type: 'cim' };

/**
 * Agregované pole, v ktorom zoskupenie nesie počet značiek jedného typu -
 * podľa neho sa krája koláč.
 */
const kindCountField = (kind: FeedMarkerKind) => `${kind}_count`;

/**
 * Zhlukovanie vrstvy feedu. Je to funkcia farieb, nie konštanta: farby prídu
 * z témy a nastavuje sa na dvoch miestach - pri vytvorení vrstvy a pri
 * prekročení `CLUSTER_MAX_ZOOM`.
 *
 * Prstence hlasov v zoskupení nie sú a nemajú kde byť: koláč hovorí o mnohých
 * podnetoch naraz. Pod `CLUSTER_MAX_ZOOM` je preto vidieť len farby stavov,
 * prstence sa objavia s jednotlivými značkami.
 *
 * Zoskupenie je **koláč**: každý typ značky v ňom má kus podľa toho, koľko ich
 * doň padlo, a v strede stojí spoločný počet. Pri jedinom type vyjde koláč
 * celý jednofarebný, teda presne to koliesko s číslom, čo tu bolo predtým.
 *
 * Kusy počítajú agregované polia `<typ>_count`. Vrstva stojí na grafikách
 * z pamäte, takže sa musia zrátať Arcade výrazom nad `kind` každej značky -
 * hotové pole s počtom na typ v dátach nie je.
 *
 * Veľkosť koliesok drží zraková premenná nad `cluster_count`, nie
 * `clusterMinSize`/`clusterMaxSize`: tie platia pre východiskový vzhľad
 * zoskupenia a s vlastným rendererom sa ignorujú. Rozsah je ten istý, 40-48 px.
 */
/**
 * Polia grafiky feedu. Sú spoločné pre feed aj halo vrstvu: obe stoja na tých
 * istých grafikách (halo dostane ich klony), takže musia poznať tie isté
 * atribúty - inak by `replaceLayerFeatures` porovnávalo `undefined`.
 */
const FEED_LAYER_FIELDS: __esri.FieldProperties[] = [
  { name: 'id', type: 'oid' },
  { name: 'itemId', type: 'string' },
  { name: 'kind', type: 'string' },
  { name: 'votes', type: 'integer' },
  { name: 'haloCode', type: 'integer' },
];

/**
 * Kód prstencov jednej značky: stupeň hala a poradie stavu v jednom čísle
 * (`stupeň * 10 + index stavu`).
 *
 * Zhluk vie agregovať len čísla, a prstenec potrebuje dve veci naraz - koľko
 * ich je (hlasy) a akej sú farby (stav). Jedno číslo ich prenesie cez `max`:
 * vyhrá najhlasovanejší podnet v zhluku a pri zhode stupňa rozhodne poradie vo
 * `FEED_KINDS` (posledný, teda `resolved`, je najvyšší). Renderer potom
 * z čísla vyberá hotový symbol, nič nedopočítava.
 */
const haloCode = (kind: FeedMarkerKind, level: number) =>
  level * 10 + FEED_KINDS.indexOf(kind);

/**
 * Ten istý výpočet v Arcade, lebo zhluk počíta štatistiku na svojej strane.
 * Hranice aj poradie stavov sa berú z konštánt, takže sa obe podoby nerozídu.
 */
const haloCodeExpression = [
  `var lvl = ${VOTE_HALO_STOPS.map(
    (stop) => `IIF($feature.votes >= ${stop}, 1, 0)`,
  ).join(' + ')};`,
  `var kind = Decode($feature.kind, ${FEED_KINDS.map(
    (kind, index) => `'${kind}', ${index}`,
  ).join(', ')}, 0);`,
  'return lvl * 10 + kind;',
].join('\n');

/** Prstence pre všetky kombinácie stavu a stupňa; stupeň 0 nekreslí nič. */
const haloValueInfos = (colors: Record<FeedMarkerKind, string>) =>
  FEED_KINDS.flatMap((kind) =>
    VOTE_HALO_SIZES_PX.map((_, index) => ({
      value: haloCode(kind, index + 1),
      symbol: voteHaloSymbol(colors[kind], index + 1),
    })),
  );

/**
 * Zhlukovanie halo vrstvy. Musí mať **rovnaký `clusterRadius`** ako
 * `clusterReduction` a bežať nad **rovnakými** grafikami - zhlukovanie robí
 * každá vrstva sama za seba a koliesko sedí v ťažisku svojich členov, takže by
 * pri inom polomere alebo inej množine prstenec vyšiel vedľa koláča. Preto do
 * halo vrstvy idú všetky značky, aj tie bez hlasov: podnet bez hlasov nemá
 * symbol, ale do ťažiska patrí.
 *
 * V zhluku kreslia prstence **najhlasovanejší** podnet, nie súčet hlasov:
 * krúžok tak hovorí to isté, čo pri jednotlivej značke - „toľko hlasov má
 * jeden podnet" - a nezačne pri oddialení merať hustotu, ktorú už nesie
 * veľkosť koláča a číslo v ňom. Farbu drží stav toho istého podnetu, takže
 * halo vyzerá rovnako v zhluku aj pri priblížení.
 */
const haloClusterReduction = (
  colors: Record<FeedMarkerKind, string>,
): __esri.FeatureReductionClusterProperties & { type: 'cluster' } => ({
  type: 'cluster',
  clusterRadius: '80px',
  fields: [
    {
      name: 'halo_code',
      statisticType: 'max',
      onStatisticExpression: { expression: haloCodeExpression },
    },
  ],
  // Číslo aj popup patria koláču vo feede; halo je len podklad pod ním.
  labelingInfo: [],
  popupEnabled: false,
  renderer: {
    type: 'unique-value',
    field: 'halo_code',
    uniqueValueInfos: haloValueInfos(colors),
  },
});

const clusterReduction = (
  colors: Record<FeedMarkerKind, string>,
): __esri.FeatureReductionClusterProperties & { type: 'cluster' } => ({
  type: 'cluster',
  clusterRadius: '80px',
  fields: FEED_KINDS.map((kind) => ({
    name: kindCountField(kind),
    statisticType: 'sum',
    onStatisticExpression: {
      expression: `IIF($feature.kind == '${kind}', 1, 0)`,
    },
  })),
  renderer: {
    type: 'pie-chart',
    attributes: FEED_KINDS.map((kind) => ({
      field: kindCountField(kind),
      color: colors[kind],
    })),
    // Plný koláč, žiadna diera: v strede stojí počet a s dierou by číslo
    // sedelo na bielom kolieske, teda by zoskupenie vyzeralo inak než dnes.
    holePercentage: 0,
    // Biele hranice medzi kusmi, rovnaké ako obruba samotnej značky.
    outline: { color: 'white', width: 1 },
    visualVariables: [
      {
        type: 'size',
        field: 'cluster_count',
        // Prvý stupienok je veľkosť **obyčajnej značky**, nie najmenšie
        // zoskupenie: zhlukovanie zabalí do zoskupenia aj osamotenú značku
        // a tá musí zostať malá guľôčka. Bez neho boli všetky značky na mape
        // rovnako veľké ako zhluky. Od dvoch vyššie sú to rozmery zhluku,
        // aké mal predtým `clusterMinSize`/`clusterMaxSize`.
        stops: [
          { value: 1, size: MARKER_SIZE },
          { value: 2, size: '40px' },
          { value: 25, size: '48px' },
        ],
      },
    ],
  },
  labelingInfo: [
    {
      labelExpressionInfo: {
        expression: 'Text($feature.cluster_count, "#,###")',
      },
      symbol: {
        type: 'text',
        color: 'white',
        font: { size: 12, weight: 'bold' },
      },
      labelPlacement: 'center-center',
      // Číslo patrí ku každému zoskupeniu, aj keď sa dve prekryjú: ArcGIS by
      // inak popisok toho spodného vynechal a zostalo by z neho bezvýznamné
      // koliesko. Osamotenú značku (`cluster_count` 1) popisok nedostane.
      deconflictionStrategy: 'none',
      where: 'cluster_count > 1',
    },
  ],
  popupEnabled: false,
});

/**
 * Krúžok okolo značky, ktorej detail je otvorený v paneli, a čiarkovaná linka
 * od nej k panelu. Bez nich by z troch značiek v tom istom rohu nebolo poznať,
 * o ktorej panel hovorí.
 */
const HIGHLIGHT_LAYER = 'selected-item-layer';

/**
 * Nájdené miesto z panela hľadania - značka s popisom toho, čo na tom mieste
 * stojí. Kreslíme ju sami, hoci ju vie nakresliť aj geokóder: jeho značka
 * prichádza s popupom ArcGISu, ktorý žije v shadow DOM mapy bez našej témy,
 * a nedá sa k nej dopísať nič vlastné. Preto je v `arcgisSearch`
 * `resultGraphicEnabled: false` a značka je tu.
 */
const SEARCH_LAYER = 'search-result-layer';

/**
 * Priblíženie, na ktoré sa mapa dostane pri otvorení detailu. Rovnaké pre
 * všetky cesty k detailu (klik do mapy, zoznam, hľadanie, odkaz s `?detail=`),
 * aby sa tá istá značka neukazovala raz z výšky mesta a raz z ulice.
 */
const DETAIL_ZOOM = 17;

/**
 * O koľko pixelov **pod** stredom mapy dosadne značka otvoreného detailu.
 *
 * Presne naproti ukotveniu čiary (polovica výšky mapy) by bola čiara k panelu
 * vodorovná priamka. S odsadením z panela vždy vyjde vodorovne a zlomí sa do
 * kolena - to je tvar, ktorý čiara dostala až posunom mapy, a vyzerá lepšie.
 * Platí len na desktope: na mobile žiadna čiara nie je a odsadená značka by
 * skončila pod spodným listom.
 */
const DETAIL_OFFSET_Y = 90;

/** Kde čiara končí, aby nesadla na obrubu vybranej značky (jej polomer je 10). */
const CONNECTOR_END_GAP = 14;
/**
 * Vodorovný úsek pred značkou. Čiara k nej musí prichádzať **vždy sprava**, tak
 * sa druhé koleno urobí takto daleko od nej a zvyšok dobehne vodorovne.
 */
const CONNECTOR_RUN = 32;
/**
 * Vodorovný úsek od ukotvenia, na ktorom čiara vždy začína. Koleno pri detaile
 * má byť vidieť **vždy**, takže čiara z panela nikdy nevychádza šikmo - najprv
 * odbočí vodorovne a zlomí sa až tu (alebo ďalej, keď zlom vyjde na 45°).
 */
const CONNECTOR_ANCHOR_RUN = 44;
/** Zaoblenie kolena. */
const CONNECTOR_CORNER = 12;
/**
 * Najkratší vodorovný úsek od panela. Keď je značka bližšie (typicky pod
 * panelom), koncové koleno sa nekreslí - nie je preň miesto.
 */
const CONNECTOR_MIN_RUN = 40;

type ConnectorPoint = { x: number; y: number };

/**
 * Cesta cez lomené body so zaoblenými zlomami.
 *
 * Prvý a posledný bod sú konce, medziľahlé sú kolená. Každé sa zaoblí
 * `CONNECTOR_CORNER`-om, respektíve tým, čo sa doň z oboch susedných úsekov
 * zmestí - inak by pri krátkom úseku zaoblenie prestrelilo za jeho druhý konec
 * a čiara by sa vrátila sama do seba.
 */
const connectorPath = (points: ConnectorPoint[]): string => {
  const trim = (value: number) => Math.round(value * 10) / 10;
  const parts = [`M${trim(points[0].x)},${trim(points[0].y)}`];

  for (let index = 1; index < points.length - 1; index += 1) {
    const previous = points[index - 1];
    const corner = points[index];
    const next = points[index + 1];

    const inLength =
      Math.hypot(corner.x - previous.x, corner.y - previous.y) || 1;
    const outLength = Math.hypot(next.x - corner.x, next.y - corner.y) || 1;
    const radius = Math.min(CONNECTOR_CORNER, inLength / 2, outLength / 2);

    const enterX = corner.x + ((previous.x - corner.x) / inLength) * radius;
    const enterY = corner.y + ((previous.y - corner.y) / inLength) * radius;
    const exitX = corner.x + ((next.x - corner.x) / outLength) * radius;
    const exitY = corner.y + ((next.y - corner.y) / outLength) * radius;

    parts.push(`L${trim(enterX)},${trim(enterY)}`);
    parts.push(
      `Q${trim(corner.x)},${trim(corner.y)} ${trim(exitX)},${trim(exitY)}`,
    );
  }

  const last = points[points.length - 1];
  parts.push(`L${trim(last.x)},${trim(last.y)}`);

  return parts.join(' ');
};

/**
 * Stred mapy tak, aby značka dosadla `offsetY` pixelov pod ním.
 *
 * Odsadenie je v pixeloch, ale `goTo` chce stred v súradniciach, takže sa
 * prepočíta rozlíšením **v cieľovom** priblížení: úrovne podkladu idú po
 * mocninách dvojky, takže je to presne `resolution * 2^(zoom - DETAIL_ZOOM)`.
 * Keď mapa ešte nestojí na svojom priblížení alebo nie je vo web mercatore,
 * odsadenie sa vynechá a značka pristane v strede - je to kozmetika čiary,
 * nie poloha.
 */
const detailCenter = (
  view: __esri.MapView | undefined,
  coordinates: [number, number],
  offsetY: number,
): __esri.Point | undefined => {
  if (!positioning || !view || view.zoom <= 0 || !offsetY) return undefined;
  if (!view.spatialReference?.isWebMercator) return undefined;

  const [x, y] = positioning.lngLatToXY(coordinates[0], coordinates[1]);
  const resolution = view.resolution * 2 ** (view.zoom - DETAIL_ZOOM);

  // Stred posunutý na sever = značka pod stredom obrazovky.
  return new positioning.Point({
    x,
    y: y + offsetY * resolution,
    spatialReference: view.spatialReference,
  });
};

const feedItemGraphic = async (item: IFeedItem) => {
  const { default: Graphic } = await import('@arcgis/core/Graphic');
  const kind = feedMarkerKind(item);
  return new Graphic({
    geometry: {
      type: 'point',
      longitude: item.location.coordinates[0],
      latitude: item.location.coordinates[1],
    },
    // Značka nesie identifikátor, stav a hlasy. Názov ani popis v nej netreba:
    // obsah detailu kreslí panel z položky feedu, nie mapa z atribútov.
    //
    // Tri polia o tom istom sú tu zámerne, každé pre iného konzumenta:
    // podľa `kind` krája koláč zoskupenia (Arcade výraz nad jednou hodnotou)
    // a farbí sa značka, `haloCode` vyberá prstence jednotlivého podnetu
    // a `votes` je číslo, z ktorého si zhluk ten istý kód dopočíta cez `max`.
    attributes: {
      id: item.id,
      itemId: item.id,
      kind,
      votes: item.votesCount ?? 0,
      haloCode: haloCode(kind, voteHaloLevel(item.votesCount)),
    },
  });
};

/**
 * Vrstva feedu. Je **jedna** pre všetky typy značiek a farbu robí atribút
 * `kind`.
 *
 * Kedysi ich bolo päť, jedna na typ. Každá sa ale zhlukovala sama za seba,
 * takže sa zoskupenia rôznych typov na mape prekrývali - a zoskupenie jedného
 * typu vedelo prekryť osamotenú značku iného, ktorá sa potom nedala ani
 * vidieť, ani kliknúť. V jednej vrstve prebehne zhlukovanie raz nad všetkým
 * a z každého zoskupenia je koláč, v ktorom má typ svoj kus.
 */
const feedFeatureLayer = async (
  graphics: __esri.Graphic[],
  clustered: boolean,
  colors: Record<FeedMarkerKind, string>,
) => {
  const { default: FeatureLayer } =
    await import('@arcgis/core/layers/FeatureLayer');
  return new FeatureLayer({
    source: graphics,
    objectIdField: 'id',
    fields: FEED_LAYER_FIELDS,
    // Popupy ArcGISu sú vypnuté zámerne - detail značky aj skupiny rieši
    // appka sama (`MapItemDetail`, resp. priblíženie na klik v `handleReady`).
    // Obsah popupu totiž žije v shadow DOM mapy, kam emotion svoje štýly
    // nevkladá, takže by v ňom nebola ani téma, ani komponenty z `ui-react`.
    popupEnabled: false,
    featureReduction: clustered ? clusterReduction(colors) : null,
    // Symbol je jeden na stav: prstence hlasov kreslí halo vrstva pod feedom,
    // takže tu ich netreba násobiť počtom stupňov.
    renderer: {
      type: 'unique-value',
      field: 'kind',
      uniqueValueInfos: FEED_KINDS.map((kind) => ({
        value: kind,
        symbol: feedMarkerSymbol(colors[kind]),
      })),
    },
  });
};

/**
 * Vrstva prstencov hlasov, ktorá leží **pod** feedom.
 *
 * Prstence patrili kedysi priamo značke (`feedMarkerSymbol`), lenže pod
 * `CLUSTER_MAX_ZOOM` značku nahradí koláč zhluku - a to platí aj pre osamotený
 * podnet, lebo zhlukovanie zabalí každú značku. Halo tak z mapy zmizlo hneď,
 * ako sa používateľ oddialil, teda presne tam, kde má „kde sa hlasuje" zmysel.
 *
 * Vrstva má rovnaké grafiky a rovnaký `clusterRadius` ako feed, takže jej
 * zoskupenia sedia v tých istých ťažiskách a prstenec vyjde presne pod
 * koláčom. Do `hitTest`u nevstupuje a popupy nemá - klikanie zostáva na feede.
 */
const haloFeatureLayer = async (
  graphics: __esri.Graphic[],
  clustered: boolean,
  colors: Record<FeedMarkerKind, string>,
) => {
  const { default: FeatureLayer } =
    await import('@arcgis/core/layers/FeatureLayer');
  return new FeatureLayer({
    source: graphics,
    objectIdField: 'id',
    fields: FEED_LAYER_FIELDS,
    popupEnabled: false,
    featureReduction: clustered ? haloClusterReduction(colors) : null,
    // Bez zhlukovania kreslí prstence každý podnet sám za seba - `haloCode` je
    // to isté číslo, aké zhluk vyberá cez `max`, takže obidva režimy siahajú
    // po tom istom zozname symbolov. Stupeň 0 v ňom nie je a `defaultSymbol`
    // tiež, takže podnet bez hlasov nenakreslí nič.
    renderer: {
      type: 'unique-value',
      field: 'haloCode',
      uniqueValueInfos: haloValueInfos(colors),
    },
  });
};

/**
 * Položka pod zásahom myši.
 *
 * Zhlukovanie zabalí do zoskupenia **každú** značku, aj osamotenú, takže
 * `hitTest` nevráti našu grafiku, ale zoskupenie s `cluster_count: 1`. To
 * `itemId` nenesie a `queryFeatures({ aggregateIds })` nad ním vracia prázdno,
 * lebo vrstva stojí na grafikách z pamäte, nie na službe. Zoskupenie jednej
 * značky má ale jej presnú polohu, takže položku nájdeme podľa nej.
 */
const hitItem = (
  items: IFeedItem[],
  hit: __esri.GraphicHit,
): IFeedItem | undefined => {
  const direct = hit.graphic.attributes?.itemId;
  if (direct) {
    return items.find(({ id }) => id === direct);
  }

  const point = hit.graphic.geometry as __esri.Point;
  if (hit.layer?.type !== 'feature' || !point) {
    return undefined;
  }

  let nearest: IFeedItem | undefined;
  let nearestDistance = Infinity;

  for (const item of items) {
    const [longitude, latitude] = item.location.coordinates;
    const distance = Math.hypot(
      longitude - point.longitude,
      latitude - point.latitude,
    );
    if (distance < nearestDistance) {
      nearestDistance = distance;
      nearest = item;
    }
  }

  return nearest;
};

/**
 * Položky, ktoré tvoria kliknuté zoskupenie - presne tie, ktoré ArcGIS do
 * neho zabalil (moj-presov#13).
 *
 * Pýta sa **pohľadu vrstvy** (`layerView.queryFeatures` s `aggregateIds`),
 * nie vrstvy samej: vrstva stojí na grafikách z pamäte a jej dotaz o
 * zoskupeniach nevie nič, pohľad ich ale kreslí, takže ich členov pozná.
 * Do októbra 2026 sa členovia dopočítavali podľa vzdialenosti od ťažiska
 * (všetko do `CLUSTER_RADIUS`). Zhlukovanie je však mriežka a ťažisko suseda
 * môže byť bližšie než 80 px, takže klik na zhluk 13 vybral 21 podnetov
 * a dva susedné zhluky sa v paneli prekrývali.
 *
 * `undefined` = dotaz nič nevrátil alebo zlyhal; vtedy nastupuje
 * [nearestClusterItems].
 */
const exactClusterItems = async (
  view: __esri.MapView,
  hit: __esri.GraphicHit,
  items: IFeedItem[],
): Promise<IFeedItem[] | undefined> => {
  const layer = hit.layer as __esri.FeatureLayer | undefined;
  const aggregateId =
    hit.graphic.attributes?.aggregateId ?? hit.graphic.getObjectId();
  if (!layer || aggregateId === undefined || aggregateId === null) {
    return undefined;
  }

  try {
    const layerView = (await view.whenLayerView(
      layer,
    )) as __esri.FeatureLayerView;
    const query = layerView.createQuery();
    query.aggregateIds = [aggregateId];
    query.outFields = ['itemId'];
    const { features } = await layerView.queryFeatures(query);
    const ids = new Set(
      features.map((feature) => feature.attributes?.itemId).filter(Boolean),
    );
    if (!ids.size) return undefined;
    return items.filter(({ id }) => ids.has(id));
  } catch {
    return undefined;
  }
};

/**
 * Záloha k [exactClusterItems]: `count` položiek najbližších k ťažisku
 * zoskupenia na obrazovke. Počet je ten, ktorý zoskupenie samo hlási
 * (`cluster_count`), takže panel nikdy neukáže viac podnetov, než je na
 * koliesku - pri polomere (`CLUSTER_RADIUS`) ich bolo aj o polovicu viac.
 */
const nearestClusterItems = (
  view: __esri.MapView,
  items: IFeedItem[],
  center: __esri.Point,
  count: number,
  PointClass: NonNullable<typeof positioning>['Point'],
): IFeedItem[] => {
  const origin = view.toScreen(center);
  if (!origin) return [];

  return items
    .map((item) => {
      const [longitude, latitude] = item.location.coordinates;
      const screen = view.toScreen(
        new PointClass({
          longitude,
          latitude,
          spatialReference: view.spatialReference,
        }),
      );
      return {
        item,
        distance: screen
          ? Math.hypot(screen.x - origin.x, screen.y - origin.y)
          : Infinity,
      };
    })
    .filter(({ distance }) => distance <= CLUSTER_RADIUS)
    .sort((a, b) => a.distance - b.distance)
    .slice(0, count)
    .map(({ item }) => item);
};

const replaceLayerFeatures = async (
  layer: __esri.FeatureLayer,
  newFeatures: __esri.Graphic[],
): Promise<__esri.EditsResult> => {
  const { features: oldFeatures } = await layer.queryFeatures();
  // Kľúč nesie aj vzhľad, nielen identifikátor: podnet, ktorý úrad prijme,
  // mení farbu, a podnet, ktorý dostane piaty hlas, mení počet prstencov.
  // Bez toho by značka zostala nakreslená tak, ako prišla prvýkrát. Ten istý
  // kľúč platí pre feed aj pre halo vrstvu - obe stoja na tých istých
  // grafikách.
  const diff = arrayDiff(
    oldFeatures,
    newFeatures,
    ({ attributes }) =>
      `${attributes.itemId}|${attributes.kind}|${attributes.haloCode}`,
  );
  const { deleted: deleteFeatures, added: addFeatures } = diff;
  return await layer.applyEdits({ deleteFeatures, addFeatures });
};

type Props = {
  onMapCenterChange?: (center: { lng: number; lat: number }) => void;
  /**
   * Žiadosť o presun mapy na vybranú položku - zo zoznamu, z hľadania aj
   * z kliknutia do mapy. Je to objekt, nie identifikátor, zámerne: druhé
   * kliknutie na tú istú položku musí mapu presunúť znova (napríklad keď ju
   * používateľ medzitým odsunul z výrezu), a rovnaký reťazec by sa tváril, že
   * sa nič nezmenilo.
   *
   * Bez nej sa mapa vycentruje podľa samotného výberu - to pokrýva odkaz
   * s `?detail=`, kde žiadne kliknutie neprebehlo.
   */
  focusRequest?: { id: string };
  /**
   * Klik na značku, resp. na prázdnu mapu (`undefined`). Detail vykresľuje
   * stránka, nie mapa - popupy ArcGISu sú vypnuté.
   *
   * Posiela sa celá položka, nie len identifikátor: mapa ju v ruke už má
   * a stránka by ju musela hľadať v tom istom zozname znova. Kým to tak bolo,
   * dala sa appka zaseknúť - identifikátor, ktorý stránka vo svojom zozname
   * nenašla, zapísal do adresy detail, ktorý sa nemal ako otvoriť, a ďalšie
   * kliknutia do tej istej značky už adresu nemenili, teda nerobili nič.
   */
  onSelectItem?: (item?: IFeedItem) => void;
  /**
   * Vybraná položka. Detail k nej kreslí panel (`FeedDetail`), mapa z nej
   * kreslí len prepojenie: obrubu na značke a čiarkovanú linku k panelu.
   */
  selectedItem?: IFeedItem;
  /**
   * Klik na zoskupenie. Namiesto priblíženia zúži zoznam na to, čo je v ňom -
   * priblíženie by zoskupenie rozpadlo a používateľ by prišiel o to, na čo
   * klikol. `undefined` = klik mimo zoskupenia, teda zrušenie výberu.
   */
  onSelectCluster?: (itemIds?: string[]) => void;
  onSearchLocationChange?: (location: string) => void;
  /**
   * Vybraný podnet z panela hľadania. Mapa ho vie len ohlásiť - detail
   * otvára stránka, rovnako ako pri kliknutí na značku.
   */
  onSearchReport?: (reportId: string) => void;
  /** `lon,lat` výrezu; hľadanie podľa neho zoradí nájdené podnety. */
  searchLocation?: string;
  /**
   * Akcia vedľa hľadania (desktop). Na hlavnej stránke je to tlačidlo nového
   * podnetu: stojí v tom istom riadku ako hľadanie, nie v rohu mapy. Kreslí
   * ho stránka, lebo mapa o podávaní podnetov nič nevie - dostane hotový uzol.
   */
  searchAction?: React.ReactNode;
  /**
   * Žiadosť o návrat na priblíženie, ktoré bolo pred otvorením detailu.
   * Posiela ju stránka pri zatvorení panela; klik do prázdnej mapy si ju mapa
   * spustí sama. Je to počítadlo, nie príznak: druhé zatvorenie musí mapu
   * vrátiť znova.
   */
  restoreZoomToken?: number;
  /** Pohľad, keď je mapa pripravená - detail z neho berie geokóder na adresu. */
  onViewReady?: (view: __esri.MapView) => void;
  feedsWithLocation: IFeedItem[];
  forceShowMap?: boolean;
  isLoading?: boolean;
  onZoom?: (zoom: number) => void;
  initialCenter?: MapCenter;
  initialZoom?: number;
  /**
   * Miesto, na ktoré sa má mapa presunúť hneď, ako je pripravená - poloha
   * z adresy (`?plusCode=`, `?lat=&lon=`). `initialCenter` a `initialZoom` na
   * to nestačia: čítajú sa pri prvom renderi, a to je stav z adresy ešte
   * v efekte stránky, nie v nich. Nový objekt znamená nový let.
   */
  placeRequest?: { center: [number, number]; zoom: number };
  /**
   * Časť mapy, ktorú prekrýva panel stránky (desktop vpravo, mobil dole).
   * Ide do `view.padding`, takže `view.center` - a s ním zoradenie „od
   * najbližších", let na značku aj na nájdené miesto - je stred **voľnej**
   * plochy, nie bod pod panelom.
   */
  viewPadding?: { right?: number; bottom?: number };
  /** Pevný pin v strede voľnej plochy ([MapCenterPin]). */
  showCenterPin?: boolean;
};

const FeedsMap: React.FC<Props> = ({
  onMapCenterChange,
  focusRequest,
  onSelectItem,
  selectedItem,
  onSelectCluster,
  onSearchLocationChange,
  onSearchReport,
  searchLocation,
  searchAction,
  restoreZoomToken,
  onViewReady,
  feedsWithLocation,
  forceShowMap,
  isLoading = false,
  onZoom,
  initialCenter,
  initialZoom,
  placeRequest,
  viewPadding,
  showCenterPin = false,
}) => {
  const paddingRight = viewPadding?.right ?? 0;
  const paddingBottom = viewPadding?.bottom ?? 0;
  const { isMd } = useResponsive();
  const { palette } = useTheme();

  /**
   * Farby značiek. Z témy, teda tie isté, aké má legenda pod mapou - preto sa
   * berú tu a nie v konštantách nad súborom, kam sa téma nedostane.
   */
  const colors = React.useMemo(() => feedKindColors(palette), [palette]);

  const mapRef = React.useRef<HTMLArcgisMapElement>(null);

  // Poslúchač kliknutia sa vešia raz, keď je pohľad pripravený, takže by si
  // zapamätal prvú verziu `onSelectItem` a ďalšie by mu už neprišli. Ref drží
  // vždy tú aktuálnu.
  const onSelectItemRef = React.useRef(onSelectItem);
  React.useEffect(() => {
    onSelectItemRef.current = onSelectItem;
  }, [onSelectItem]);

  const onSelectClusterRef = React.useRef(onSelectCluster);
  React.useEffect(() => {
    onSelectClusterRef.current = onSelectCluster;
  }, [onSelectCluster]);

  // Z toho istého dôvodu drží ref aj položky: klik ich potrebuje aktuálne.
  const feedsRef = React.useRef(feedsWithLocation);
  React.useEffect(() => {
    feedsRef.current = feedsWithLocation;
  }, [feedsWithLocation]);

  const selectedItemRef = React.useRef(selectedItem);
  React.useEffect(() => {
    selectedItemRef.current = selectedItem;
  }, [selectedItem]);

  /**
   * Vybraná položka rozobraná na hodnoty, ktoré sa nemenia s každým dotazom.
   *
   * Feed sa pri posune mapy doťahuje znova, takže tá istá položka príde ako
   * nový objekt. Efekty pod tým sa naň nesmú viazať: značka aj čiara by
   * sa pri každom dotaze skladali odznova, teda by na chvíľu zmizli.
   */
  const selectedId = selectedItem?.id;
  const [selectedLongitude, selectedLatitude] = selectedItem?.location
    ?.coordinates ?? [undefined, undefined];
  const selectedColor = selectedItem
    ? colors[feedMarkerKind(selectedItem)]
    : undefined;

  /**
   * Priblíženie a stred, na ktorých mapa stála pred otvorením detailu.
   *
   * Detail si mapu priblížil sám (`DETAIL_ZOOM`), tak ju pri zatvorení aj
   * vráti - bez toho zostal používateľ po zavretí panela v ulici, do ktorej sa
   * nepriblížil. **Stred sa vracia len vtedy, keď s mapou počas detailu
   * nehýbal používateľ** (`movedInDetail`): kto ju medzitým odsunul inam,
   * toho by skok späť zmiatol, akoby appka zabudla, kam sa pozerá. Kto ju
   * nechal tak, ten sa vracia k zoznamu, z ktorého detail otvoril
   * (moj-presov#24) - a ten zoznam je zoradený podľa pôvodného stredu.
   *
   * Kým je nastavený, je detail otvorený: let do neho je krok appky, nie
   * používateľa, takže feed sa podľa neho nedoťahuje ani nezoraďuje
   * (`handleMove`), inak by sa po návrate ukázal iný zoznam.
   */
  const viewBeforeDetail = React.useRef<
    { zoom: number; center: __esri.Point } | undefined
  >(undefined);

  /**
   * Pohol s mapou počas detailu používateľ - ťahom, kolieskom, klávesnicou,
   * dvojklikom či výberom miesta v hľadaní? Rozhoduje sa podľa udalostí, nie
   * podľa toho, či mapa stojí tam, kam ju posadil let: prvý pokus porovnával
   * polohy, lenže let, ktorý ArcGIS preruší, polohu nezapísal a feed sa po
   * ňom aj tak prenačítal - zoznam po návrate začínal navrchu.
   */
  const movedInDetail = React.useRef(false);

  /** Predošlá žiadosť o presun - podľa nej sa pozná zatváranie detailu. */
  const previousFocusRequest = React.useRef(focusRequest);

  const restoreViewBeforeDetail = React.useCallback(() => {
    const before = viewBeforeDetail.current;
    const returnCenter = !movedInDetail.current;
    viewBeforeDetail.current = undefined;
    movedInDetail.current = false;
    if (!before) return;

    // Animovane, tou istou dĺžkou ako let do detailu - oddialenie skokom
    // pôsobilo proti plynulému priblíženiu ako chyba.
    //
    // Do septembra 2026 tu bolo `animate: false`: po lete mapy prepnutie
    // zhlukovania rozladilo vrstvu (obrie odrezané prstence, neklikateľné
    // značky). Dnes sa pri zmene zhlukovania vrstvy **stavajú nanovo** (efekt
    // nad `feedLayerRef`), takže let už nemá čo pokaziť.
    mapRef.current
      ?.goTo(
        returnCenter
          ? { center: before.center, zoom: before.zoom }
          : { zoom: before.zoom },
        { duration: 350 },
      )
      .catch(() => undefined);
  }, []);

  // Zatvorenie panela hlási stránka; klik do prázdnej mapy si návrat spustí
  // priamo v poslúchači kliknutia. Počítadlo z prvého vykreslenia (0) žiadny
  // návrat neznamená.
  React.useEffect(() => {
    if (!restoreZoomToken) return;
    restoreViewBeforeDetail();
  }, [restoreZoomToken, restoreViewBeforeDetail]);

  /**
   * Vrstva zvýraznenia. `hitTest` ju potrebuje v zozname vrstiev: značka
   * vybranej položky je v nej, nie vo vrstvách feedu.
   */
  const highlightLayerRef = React.useRef<__esri.GraphicsLayer | undefined>(
    undefined,
  );

  const connectorRef = React.useRef<SVGSVGElement>(null);
  const connectorPathRef = React.useRef<SVGPathElement>(null);

  /**
   * Čiarkovaná čiara od panela k vybranej značke.
   *
   * Ukotvená je **pevne**: začína na ľavej hrane panela v polovici výšky mapy,
   * teda presne naproti vycentrovanej značke, kde na ňu čaká biely výstupok
   * panela. Kým sa mapa nepohne, je to vodorovná čiara; po posune značka z tej
   * výšky zíde a vzniknú dve kolená - šikmé v strede a vodorovné pred značkou.
   * K značke totiž čiara musí prichádzať **vždy sprava**, aby nebolo treba
   * šípku a aby posun mapy nemenil smer, ktorým do značky vchádza.
   *
   * Tvar ide priamo do atribútu `d`, nie cez stav: pri ťahaní mapy sa
   * prepočítava na každý snímok a prekresľovať pri tom komponentu Reactom by
   * bolo cítiť. Beží ako `useLayoutEffect`, aby čiara stála ešte pred
   * vykreslením - inak by na jeden snímok blikla v rohu mapy.
   *
   * Ukotvenie berie ľavú hranu panela z `--feed-panel-width`, ktorú `HomePage`
   * vystavuje (vzdialenosť od pravého okraja okna). Tá istá premenná odsádza
   * cookie banner, takže sa nedopočítava druhýkrát.
   */
  React.useLayoutEffect(() => {
    const view = mapRef.current?.view;

    if (
      !isMd ||
      !view ||
      selectedLongitude === undefined ||
      selectedLatitude === undefined
    ) {
      return;
    }

    let isCancelled = false;
    let handle: { remove: () => void } | undefined;

    const start = ({ Point, watch }: NonNullable<typeof positioning>) => {
      if (isCancelled) return;

      const point = new Point({
        longitude: selectedLongitude,
        latitude: selectedLatitude,
      });

      const update = () => {
        const element = connectorRef.current;
        const path = connectorPathRef.current;
        if (!element || !path) return;

        const panelWidth =
          parseFloat(
            getComputedStyle(document.documentElement).getPropertyValue(
              '--feed-panel-width',
            ),
          ) || 0;
        const startX = Math.round(Math.max(0, view.width - panelWidth));
        const startY = Math.round(view.height / 2);

        const screen = view.toScreen(point);
        if (!screen) return;

        element.style.visibility = 'visible';

        const anchor = { x: startX, y: startY };
        const rise = Math.abs(screen.y - startY);

        /**
         * Koncové koleno a vodorovný príchod k značke sa dajú postaviť len na
         * značku, ktorú je vidieť a ktorá má od panela dosť miesta. Za hranou
         * mapy čiara pokračuje **priamo na ňu** a hranu len prekročí - SVG ju
         * odstrihne, takže na kraji je vidieť rovnú čiaru pokračujúcu za mapu,
         * nie zalomenie, ktoré sa nikde nekončí. Zmizne až so zatvorením
         * detailu; kým sa skrývala, posunutá mapa vyzerala, akoby prepojenie
         * s panelom prestalo existovať.
         */
        const canArriveFromRight =
          screen.x >= 0 &&
          screen.y >= 0 &&
          screen.y <= view.height &&
          screen.x + CONNECTOR_END_GAP <= startX - CONNECTOR_MIN_RUN;

        if (canArriveFromRight) {
          const end = {
            x: screen.x + CONNECTOR_END_GAP,
            y: screen.y,
          };

          if (rise < 1) {
            // Vycentrovaná značka leží naproti ukotveniu - koleno netreba.
            path.setAttribute(
              'd',
              `M${startX},${startY} H${Math.round(end.x)}`,
            );
            return;
          }

          // Koncové koleno stojí `CONNECTOR_RUN` pred značkou, takže k nej
          // čiara prichádza vždy sprava a nepotrebuje šípku.
          const run = { x: end.x + CONNECTOR_RUN, y: end.y };
          // Koleno pri paneli ide na 45°, teda šikmý úsek pokryje celý výškový
          // rozdiel a na vodorovný zostane zvyšok. Keď sa 45° nezmestí (značka
          // je vyššie, než je k nej ďaleko), zlomí sa v pevnej vzdialenosti od
          // panela a šikmý úsek je strmší - vidieť ho treba vždy.
          const bendX = run.x + rise;
          const knee = {
            x:
              bendX + CONNECTOR_CORNER < startX
                ? bendX
                : // Koleno musí zostať **medzi** panelom a koncovým úsekom,
                  // inak by k nemu šikmý úsek viedol doprava a čiara by urobila
                  // hák. Keď sa doň 44 px nezmestí, ide do polovice medzery.
                  Math.max(startX - CONNECTOR_ANCHOR_RUN, (startX + run.x) / 2),
            y: startY,
          };

          path.setAttribute('d', connectorPath([anchor, knee, run, end]));
          return;
        }

        // Koleno pri detaile zostáva aj pri značke za hranou: čiara z panela
        // najprv odbočí vodorovne a až v kolene zamieri na značku.
        const knee = { x: startX - CONNECTOR_ANCHOR_RUN, y: startY };
        const dx = screen.x - knee.x;
        const dy = screen.y - knee.y;
        const distance = Math.hypot(dx, dy) || 1;
        // Koniec pred krúžkom značky; pri značke za hranou je to jedno, ten kus
        // čiary je aj tak odstrihnutý.
        const t = Math.max(0, 1 - CONNECTOR_END_GAP / distance);
        const end = { x: knee.x + dx * t, y: knee.y + dy * t };

        path.setAttribute(
          'd',
          // Značka **vpravo** od kolena je pod panelom, takže by čiara musela
          // urobiť hák späť pod neho. Vtedy sa kreslí priamo - aj tak ju celú
          // zakryje panel.
          end.x >= knee.x
            ? connectorPath([anchor, end])
            : connectorPath([anchor, knee, end]),
        );
      };

      update();
      handle = watch(() => view.extent, update);
    };

    if (positioning) {
      start(positioning);
    } else {
      positioningModules.then(start);
    }

    return () => {
      isCancelled = true;
      handle?.remove();
    };
  }, [isMd, selectedLongitude, selectedLatitude]);

  /**
   * Vybraná značka s obrubou.
   *
   * Vlastná `GraphicsLayer`, nie prvok vo vrstvách feedu: tie majú zapnuté
   * zhlukovanie, ktoré by zvýraznenie zabalilo do zoskupenia a namiesto neho by
   * pribudla značka s číslom.
   *
   * **Kreslí aj samu značku**, a to navrch nad vrstvami feedu. Zhlukovanie
   * totiž zabalí každú značku a zoskupenie leží v ťažisku svojich členov, nie
   * na ich súradniciach - pri susedovi bližšie ako 60 px tak zvýraznenie stálo
   * vedľa značky a pri väčšom zhluku úplne mimo nej, teda zvýrazňovalo prázdno.
   * Vyzeralo to ako chyba pozicovania, pritom bola inde tá značka.
   *
   * Nad `CLUSTER_MAX_ZOOM` (a teda aj v `DETAIL_ZOOM`) zhlukovanie nie je,
   * takže pri kliknutí do značky to nenastane - nastane ale pri odkaze
   * s `?detail=`, ktorý mapu vráti do zhlukovaného priblíženia, a pri oddialení
   * s otvoreným detailom. Preto sa značka prekresľuje navrchu ďalej.
   *
   * Z vrstiev feedu sa položka **nevyberá**: zoskupenie ju síce počíta aj
   * v prípade, že leží vedľa neho zvýraznená, ale zásah do vrstiev feedu za to
   * nestojí. Vyberanie cez `applyEdits` je odskúšané a s dátami je v poriadku
   * (ArcGIS reťazcové `id` v `objectIdField` ignoruje a priradí vlastné číselné
   * OID - odmerané, aj vrátený prvok sa vykreslí aj zasiahne `hitTest`om), len
   * to bola práca navyše pri každej zmene výberu.
   */
  React.useEffect(() => {
    const map = mapRef.current?.map;

    if (
      !map ||
      !selectedId ||
      selectedLongitude === undefined ||
      selectedLatitude === undefined
    ) {
      return;
    }

    let isCancelled = false;

    Promise.all([
      import('@arcgis/core/layers/GraphicsLayer'),
      import('@arcgis/core/Graphic'),
    ]).then(([{ default: GraphicsLayer }, { default: Graphic }]) => {
      if (isCancelled) return;

      const geometry = {
        type: 'point' as const,
        longitude: selectedLongitude,
        latitude: selectedLatitude,
      };
      const layer = new GraphicsLayer({ id: HIGHLIGHT_LAYER });
      layer.add(
        new Graphic({
          geometry,
          // `itemId` kvôli `hitTest`u: klik do vybranej značky ju má
          // vycentrovať, nie zavrieť jej detail.
          attributes: { itemId: selectedId },
          /**
           * Jedna značka s **obrubou**, nie značka a k nej krúžok.
           *
           * Krúžok bol samostatný prvok o veľkosti 18 (v ArcGISe **bodov**, teda
           * 24 px) a medzi ním a značkou zostala biela medzera - odmerané na
           * snímke: 16 px puk, 4 px medzera, 26 px prstenec. Čítalo sa to ako
           * dve guličky na jednom mieste. Obruba drží na značke, takže je z nej
           * jeden útvar.
           *
           * Veľkosť je predvolená, ako v rendereri vrstiev feedu - odlišuje ju
           * obruba, nie veľkosť. Obruba je hrubá 3 body, takže prekryje aj bielu
           * obrysovú linku značky, ktorú pod ňou stále kreslí vrstva feedu.
           *
           * **Obruba je tmavá, nie červená.** Výplň je farba stavu položky
           * a odkedy sa tie farby berú z témy, je červená obruby `primary.main`
           * a výplň nového podnetu **tá istá farba** - predtým boli aspoň na
           * nerozoznanie (`(247,53,51)` vedľa `(230,65,55)`), dnes by sa na
           * najčastejšej značke na mape zvýraznenie stratilo úplne. Tmavá číta
           * proti všetkým štyrom výplniam aj proti podkladu `gray-vector`
           * a drží konvenciu témy: červená je farba primárnej akcie, nie stavu
           * (`FilterBar` má z rovnakého dôvodu čierne aj zapnutý filter, aj
           * pilulku počtu). Podľa farby položky obruba ísť nemôže - splynula by
           * s výplňou vždy.
           */
          symbol: {
            type: 'simple-marker',
            color: selectedColor,
            outline: { color: palette.grey[900], width: 3 },
          },
        }),
      );
      map.add(layer);
      highlightLayerRef.current = layer;
    });

    return () => {
      isCancelled = true;
      highlightLayerRef.current = undefined;
      const layer = map.findLayerById(HIGHLIGHT_LAYER);
      if (layer) map.remove(layer);
    };
  }, [selectedId, selectedLongitude, selectedLatitude, selectedColor, palette]);

  /**
   * Značka nájdeného miesta.
   *
   * Popis nesie samotná značka ako text s bielym halom, nie popup - popis je
   * jeden riadok („Rybárska", „8GX3262Q+PP") a na jeden riadok nie je vrstva
   * s vlastným DOM potrebná.
   *
   * Ruší ju klik kamkoľvek do mapy (rieši to `immediate-click` nižšie) a ďalšie
   * hľadanie, ktoré ju prepíše. V zozname pre `hitTest` **nie je**: klik do nej
   * je klik do prázdna, teda to isté ako klik vedľa nej.
   */
  const [searchResult, setSearchResult] = React.useState<
    { coordinates: [number, number]; label: string } | undefined
  >(undefined);

  React.useEffect(() => {
    const map = mapRef.current?.map;

    if (!map || !searchResult) return;

    let isCancelled = false;

    Promise.all([
      import('@arcgis/core/layers/GraphicsLayer'),
      import('@arcgis/core/Graphic'),
    ]).then(([{ default: GraphicsLayer }, { default: Graphic }]) => {
      if (isCancelled) return;

      const geometry = {
        type: 'point' as const,
        longitude: searchResult.coordinates[0],
        latitude: searchResult.coordinates[1],
      };

      const layer = new GraphicsLayer({ id: SEARCH_LAYER });
      layer.addMany([
        new Graphic({
          geometry,
          // Tmavá, nie červená: červená na mape patrí dátam (podnetom), toto je
          // stav UI - to isté rozhodnutie ako pri obrube vybranej značky.
          symbol: {
            type: 'simple-marker',
            size: 9,
            color: palette.grey[900],
            outline: { color: 'white', width: 2 },
          },
        }),
        new Graphic({
          geometry,
          symbol: {
            type: 'text',
            text: searchResult.label,
            color: palette.grey[900],
            haloColor: 'white',
            haloSize: 2,
            font: { size: 11, weight: 'bold' },
            // Nad značkou, nie v nej: `yoffset` je v bodoch a rastie nahor.
            yoffset: 11,
          },
        }),
      ]);
      map.add(layer);
    });

    return () => {
      isCancelled = true;
      const layer = map.findLayerById(SEARCH_LAYER);
      if (layer) map.remove(layer);
    };
  }, [searchResult, palette]);

  const [loadingMapLayers, setLoadingMapLayers] = React.useState(true);
  const [view, setView] = React.useState<__esri.MapView | null>(null);
  const isMapLoading = !forceShowMap && loadingMapLayers;

  /**
   * Mapa je prvýkrát nakreslená - `view.updating` po pripravenom pohľade
   * prvýkrát spadne. Pin na ňu čaká: nad prázdnou plochou, kým sa ťahajú
   * dlaždice podkladu, visel samotný v bielom a vyzeral ako chyba.
   */
  const [isMapDrawn, setIsMapDrawn] = React.useState(false);
  React.useEffect(() => {
    if (!view) return;
    let isCancelled = false;
    import('@arcgis/core/core/reactiveUtils')
      .then(({ whenOnce }) => whenOnce(() => !view.updating))
      .then(() => {
        if (!isCancelled) setIsMapDrawn(true);
      })
      .catch(() => undefined);
    return () => {
      isCancelled = true;
    };
  }, [view]);

  // Let na miesto z adresy. Čaká na `view` - odkaz z plus kódu prichádza
  // spravidla na studenú stránku, kde sa mapa ešte len načítava.
  React.useEffect(() => {
    if (!view || !placeRequest) return;
    mapRef.current
      ?.goTo(
        { center: placeRequest.center, zoom: placeRequest.zoom },
        { duration: 0 },
      )
      .catch(() => undefined);
  }, [view, placeRequest]);

  /**
   * Okraj pohľadu pod panelom. ArcGIS pri zmene `padding` drží `view.center`
   * na mieste, takže sa mapa raz posunie do voľnej plochy - preto sa okraj
   * nemení s vytiahnutím listu na mobile (to by mapou skákalo), ale je to
   * výška **zatvoreného** listu.
   */
  React.useEffect(() => {
    if (!view) return;
    view.padding = {
      top: 0,
      left: 0,
      right: paddingRight,
      bottom: paddingBottom,
    };
  }, [view, paddingRight, paddingBottom]);

  /**
   * Otvorený detail dá svoju značku do stredu mapy a priblíži na `DETAIL_ZOOM`.
   *
   * Platí to pre **každú** cestu k detailu - klik do mapy, výber v zozname,
   * hľadanie aj odkaz s `?detail=`. Preto sa presun neviaže len na
   * `focusRequest`, ktorý posiela stránka pri kliknutí, ale aj na samotný
   * výber: pri odkaze z notifikácie žiadne kliknutie neprebehlo a značka by
   * inak zostala kdekoľvek vo výreze, prípadne mimo neho.
   *
   * Krátky let zámerne: prepojenie s panelom sa ukáže, až keď značka priletí
   * do výrezu, takže dĺžka animácie je presne to, ako dlho po kliknutí nič nie
   * je vidieť. Predvolený let ArcGISu je pri väčšej vzdialenosti aj vyše
   * sekundy.
   */
  React.useEffect(() => {
    const previousRequest = previousFocusRequest.current;
    previousFocusRequest.current = focusRequest;

    /**
     * Zmiznutá žiadosť znamená zatváranie detailu, nie let.
     *
     * Adresu (`?detail=`) mení router v prechode, kým `focusRequest` je obyčajný
     * stav, takže sa zatvorenie panela vykreslí na dva razy: najprv bez žiadosti
     * a **ešte s vybranou položkou**, až potom bez oboch. Bez tejto podmienky
     * ten prvý priebeh odletel naspäť na značku, teda presne cez návrat
     * priblíženia, ktorý sa v tej istej chvíli rozbehol - zvonku to vyzeralo,
     * že sa mapa oddialiť pokúsi a hneď sa vráti.
     */
    if (!focusRequest && previousRequest) return;

    /**
     * Výber zmizol, žiadosť zostala - detail zavrela adresa, nie panel:
     * tlačidlo „späť" v prehliadači alebo gesto na telefóne. Žiadosť je
     * stará, let k nej by mapu vrátil na značku, feed by sa prenačítal pre
     * jej okolie a zoznam by začínal navrchu. Návrat pohľadu v takom prípade
     * žiada stránka (`restoreZoomToken`), rovnako ako šípka v paneli.
     */
    if (focusRequest && focusRequest === previousRequest && !selectedItem) {
      return;
    }

    // Položku k `focusRequest`u hľadáme cez ref, aby presun nespúšťalo každé
    // prekreslenie feedu, ale naozaj len nová žiadosť.
    const item = focusRequest
      ? feedsRef.current.find(({ id }) => id === focusRequest.id)
      : selectedItem;
    const coordinates = item?.location?.coordinates;

    if (!coordinates || !mapRef.current) return;

    const view = mapRef.current.view;

    // Pohľad, na ktorý sa mapa vráti po zatvorení detailu. Berie sa len raz,
    // pri prvom lete: preklik na inú značku je stále to isté ponorenie sa do
    // detailov, takže sa vracia tam, odkiaľ začalo.
    if (
      viewBeforeDetail.current === undefined &&
      view?.center &&
      view.zoom > 0
    ) {
      viewBeforeDetail.current = {
        zoom: view.zoom,
        center: view.center.clone(),
      };
    }
    movedInDetail.current = false;

    mapRef.current
      .goTo(
        {
          // Značka nesadá do stredu, ale pod neho - z toho vznikne koleno na
          // čiare k panelu. Na mobile žiadna čiara nie je, tak ani odsadenie.
          center:
            detailCenter(view, coordinates, isMd ? DETAIL_OFFSET_Y : 0) ??
            coordinates,
          zoom: DETAIL_ZOOM,
        },
        { duration: 350 },
      )
      // Prerušený let (ďalšie `goTo`, ťahnutie mapy) sa v ArcGISe hlási
      // odmietnutím sľubu; nie je to chyba, ktorú by malo cenu hlásiť.
      .catch(() => undefined);

    // Zámerne len `selectedItem?.id`: feed sa pri posune mapy doťahuje znova,
    // takže tá istá položka príde ako nový objekt a mapa by sa vracala na
    // značku vždy, keď používateľ odíde inam.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusRequest, selectedItem?.id, isMd]);

  /** Vrstva so značkami feedu; jedna pre všetky typy (pozri `feedFeatureLayer`). */
  const feedLayerRef = React.useRef<__esri.FeatureLayer | undefined>(undefined);
  /**
   * Či sa má zhlukovať - `view.zoom < CLUSTER_MAX_ZOOM` po dolete mapy.
   * `undefined`, kým mapa nie je pripravená; vrstva potom vznikne podľa
   * aktuálneho priblíženia. Zmenu vrstvy nerobí prepínač, ale efekt, ktorý
   * vrstvy stavia - viď komentár pri ňom.
   */
  const [clustered, setClustered] = React.useState<boolean>();
  /** Vrstva prstencov hlasov pod feedom (pozri `haloFeatureLayer`). */
  const haloLayerRef = React.useRef<__esri.FeatureLayer | undefined>(undefined);

  React.useEffect(() => {
    if (!mapRef.current) return;

    let isCancelled = false;

    const sync = async () => {
      const features = await Promise.all(
        feedsWithLocation.map((item) => feedItemGraphic(item)),
      );
      if (isCancelled) return;

      const wanted =
        clustered ?? (mapRef.current?.view?.zoom ?? 0) < CLUSTER_MAX_ZOOM;

      const layer = feedLayerRef.current;
      // Vrstva so správnym zhlukovaním (alebo prázdny feed, pri ktorom na
      // zhlukovaní nezáleží) len dostane nové dáta.
      if (layer && (!!layer.featureReduction === wanted || !features.length)) {
        await replaceLayerFeatures(layer, features);
        // Halo stojí na klonoch tých istých grafík: jedna `Graphic` patrí vždy
        // len jednej vrstve, takže zdieľať sa nedajú.
        if (haloLayerRef.current) {
          await replaceLayerFeatures(
            haloLayerRef.current,
            features.map((feature) => feature.clone()),
          );
        }
        if (!isCancelled) setLoadingMapLayers(false);
        return;
      }

      // Prázdna vrstva sa nezakladá - vznikne s prvými dátami.
      if (!features.length) return;

      /**
       * Zmena zhlukovania = **nové vrstvy**, nie prepnutie `featureReduction`
       * na tých, ktoré už kreslia.
       *
       * Prepínanie na mieste je presne tá chvíľa, v ktorej ArcGIS (4.34)
       * vrstvu rozladí: po lete mapy (zatvorenie detailu s animovaným
       * oddialením) zostali prstence hlasov a koláče nakreslené v obrovskej
       * veľkosti, odrezané na hranách dlaždíc, a do značiek sa nedalo kliknúť.
       * Z toho stavu ju podľa merania v `.docs/vyvoj/mapa.md` nevytiahne nič
       * okrem vytvorenia nanovo - tak sa vytvára rovno.
       *
       * Staré vrstvy odchádzajú až potom, ako sa nové vykreslia (`updating`
       * spadne na `false`), aby medzi nimi nebolo prázdne okno bez značiek.
       */
      const previous = {
        feed: feedLayerRef.current,
        halo: haloLayerRef.current,
      };

      const [halo, created] = await Promise.all([
        haloFeatureLayer(
          features.map((feature) => feature.clone()),
          wanted,
          colors,
        ),
        feedFeatureLayer(features, wanted, colors),
      ]);

      // Kontrola či mapa stále existuje
      if (!mapRef.current?.map || isCancelled) return;

      // Halo ide na spodok mapy, aby ho neprekryl podklad ani značky.
      mapRef.current.map.add(halo, 0);
      haloLayerRef.current = halo;
      mapRef.current.map.add(created);
      feedLayerRef.current = created;

      const removePrevious = () => {
        if (previous.feed) mapRef.current?.map?.remove(previous.feed);
        if (previous.halo) mapRef.current?.map?.remove(previous.halo);
      };

      try {
        const layerView = (await Promise.race([
          mapRef.current.view.whenLayerView(created),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Layer view timeout')), 5000),
          ),
        ])) as __esri.FeatureLayerView;

        // Dokreslenie novej vrstvy, najviac 3 s - aby stará nezostala visieť,
        // keby sa `updating` z nejakého dôvodu nepohol.
        await new Promise<void>((resolve) => {
          const timeout = setTimeout(resolve, 3000);
          const handle = positioning?.watch(
            () => layerView.updating,
            (updating) => {
              if (updating) return;
              clearTimeout(timeout);
              handle?.remove();
              resolve();
            },
            { initial: true },
          );
          if (!handle) resolve();
        });

        removePrevious();
        if (!isCancelled) setLoadingMapLayers(false);
      } catch (e: unknown) {
        if (!isCancelled) {
          console.warn('Layer view creation cancelled or failed:', e);
        }
        // Nová vrstva sa nevytvorila - ostanú staré, ak nejaké boli.
        if (mapRef.current?.map) {
          mapRef.current.map.remove(created);
          mapRef.current.map.remove(halo);
        }
        feedLayerRef.current = previous.feed;
        haloLayerRef.current = previous.halo;
      }
    };

    sync().catch((error: unknown) => {
      if (!isCancelled) {
        console.warn('Feed layer sync failed:', error);
      }
    });

    // Cleanup function
    return () => {
      isCancelled = true;
    };
    // `colors` a `palette` sú z témy, teda sa nemenia - v zozname sú preto, že
    // ich vrstva pri vzniku dostane, nie preto, že by sa na ne dalo prekresliť.
  }, [feedsWithLocation, colors, palette, clustered]);

  /**
   * Zhlukovanie sa nad `CLUSTER_MAX_ZOOM` vypína a pod ním vracia.
   *
   * Dôvod je pri tej konštante: v priblížení detailu zhlukovaná vrstva po
   * dvojici letov prestane odpovedať na `hitTest`, takže sa do značiek nedá
   * kliknúť. Rozhoduje sa až po dolete (`view.animation`) a samotnú zmenu
   * robí efekt vyššie **novými vrstvami** - prepnúť `featureReduction` na
   * kresliacej vrstve je presne to, čo ju rozladí.
   */
  React.useEffect(() => {
    if (!view || !positioning) return;

    const apply = () => {
      if (view.animation || view.zoom <= 0) return;
      setClustered(view.zoom < CLUSTER_MAX_ZOOM);
    };

    apply();

    const handles = [
      positioning.watch(() => view.zoom, apply),
      positioning.watch(() => !!view.animation, apply),
    ];

    return () => {
      handles.forEach((handle) => handle.remove());
    };
  }, [view]);

  const handleReady = React.useCallback(
    ({ view }: HTMLArcgisMapElement) => {
      // set initial search location
      const { longitude, latitude } = view.center;
      onSearchLocationChange?.(`${longitude},${latitude}`);

      // Pohľad potrebuje panel hľadania (uprednostní miesta v okolí výrezu)
      // aj detail v paneli (geokóder na adresu), takže ide do stavu - inak by
      // sa oba renderovali skôr, než mapa existuje, a už by sa k nemu nedostali.
      setView(view);
      onViewReady?.(view);

      // Posun mapy používateľom počas otvoreného detailu (viď
      // `movedInDetail`). Klik do značky sem nepatrí: preklik spustí nový
      // let do detailu a ten príznak aj tak vynuluje.
      view.on(['drag', 'mouse-wheel', 'key-down', 'double-click'], () => {
        if (viewBeforeDetail.current && !movedInDetail.current)
          if (viewBeforeDetail.current) movedInDetail.current = true;
      });

      // `immediate-click`, nie `click`: `click` čaká, či z kliknutia nebude
      // dvojklik, a tá pauza je presne to, čo bolo po kliknutí na značku cítiť
      // ako čakanie na detail.
      view.on('immediate-click', async (event) => {
        // Značka nájdeného miesta je dočasná - zmizne pri prvom kliknutí do
        // mapy, aj keď to kliknutie inak nič neurobí. Je to jej jediný spôsob
        // zrušenia, tak nesmie visieť na tom, či sa klikom niečo vybralo.
        setSearchResult(undefined);

        // Hľadáme vo vrstvách feedu a vo vrstve zvýraznenia; nikde inde.
        // Značka vlastnej polohy ani výsledok hľadania klikom nič nerobia.
        // Vrstva zvýraznenia v zozname byť **musí**: značka vybranej položky je
        // v nej, nie vo vrstvách feedu, takže bez nej by klik do práve
        // otvorenej značky vyzeral ako klik do prázdna a detail by zavrel
        // namiesto vycentrovania.
        const hitLayers: __esri.Layer[] = [];
        if (feedLayerRef.current) hitLayers.push(feedLayerRef.current);
        if (highlightLayerRef.current) {
          hitLayers.push(highlightLayerRef.current);
        }
        const response = await view.hitTest(
          event,
          hitLayers.length ? { include: hitLayers } : undefined,
        );
        // Zásahy prídu od najvrchnejšieho; zaujíma nás prvá grafika, nie prvý
        // zásah - pod značkou môže ležať aj podkladová vrstva. Vybraná značka
        // má pritom prednosť pred zoskupením, ktoré ju prekrýva: klik do nej má
        // vycentrovať ju, nie vybrať oblasť a zavrieť jej detail.
        const graphicHits = response.results.filter(
          (result): result is __esri.GraphicHit => result.type === 'graphic',
        );
        const hit =
          graphicHits.find((result) => result.layer?.id === HIGHLIGHT_LAYER) ??
          graphicHits[0];

        // Klik na zoskupenie viacerých značiek detail neotvára - zúži zoznam
        // v paneli na to, čo v zoskupení je. Predtým sa priblížilo o dva
        // stupne, čím sa zoskupenie rozpadlo a to, na čo sa klikalo, zmizlo.
        const clusterCount = hit?.graphic.attributes?.cluster_count ?? 0;
        if (clusterCount > 1) {
          event.stopPropagation();
          const members =
            (await exactClusterItems(view, hit, feedsRef.current)) ??
            (positioning
              ? nearestClusterItems(
                  view,
                  feedsRef.current,
                  hit.graphic.geometry as __esri.Point,
                  clusterCount,
                  positioning.Point,
                )
              : []);
          // Zoskupenie je výber oblasti, takže detail sa zavrie - ale
          // priblíženie zostane. Návrat na to pred detailom by zhluk rozpadol,
          // teda vzal to, na čo sa práve klikalo.
          viewBeforeDetail.current = undefined;
          movedInDetail.current = false;
          onSelectItemRef.current?.(undefined);
          onSelectClusterRef.current?.(
            members.length ? members.map(({ id }) => id) : undefined,
          );
          return;
        }

        // Klik mimo značiek feedu (prázdna mapa, značka vlastnej polohy,
        // výsledok hľadania) detail aj výber oblasti zavrie.
        const item = hit
          ? hitItem(
              // Vybraná položka môže z feedu vypadnúť (posun mapy zmení dotaz),
              // ale jej značka na mape zostáva - klik do nej sa nesmie zmeniť
              // na klik do prázdna.
              selectedItemRef.current
                ? [selectedItemRef.current, ...feedsRef.current]
                : feedsRef.current,
              hit,
            )
          : undefined;

        onSelectClusterRef.current?.(undefined);
        onSelectItemRef.current?.(item);

        // Zatvorenie detailu klikom do mapy vráti priblíženie rovnako ako
        // tlačidlo v paneli. Bez otvoreného detailu nie je čo vracať.
        if (!item) {
          restoreViewBeforeDetail();
        }
      });

      // Nad značkou aj nad zoskupením je ruka - obe na klik niečo robia (detail,
      // resp. priblíženie), zvyšok plochy mapy nie. Kurzor drží inline štýl
      // kontejnera mapy: plocha (`esri-view-surface`) vlastné pravidlo pre
      // `cursor` nemá, iba pre ťahanie (`data-navigating` → `grabbing`), ktoré
      // má počas posúvania prednosť, a tak to má byť.
      let isOverMarker = false;
      let hoverRequest = 0;

      const setOverMarker = (isOver: boolean) => {
        if (isOver === isOverMarker) return;
        isOverMarker = isOver;
        view.container.style.cursor = isOver ? 'pointer' : '';
      };

      view.on('pointer-move', (event) => {
        // Na dotyku kurzor neexistuje, takže hitTest na každý pohyb prsta by
        // bola len práca navyše.
        if (event.pointerType !== 'mouse') return;

        // Hľadáme len tam, kde klik niečo robí: vo vrstvách feedu a vo vrstve
        // zvýraznenia. Značka vlastnej polohy ani výsledok hľadania klikom nič
        // nerobia, tak nemajú byť ani klikateľné.
        const layers: __esri.Layer[] = [];
        if (feedLayerRef.current) layers.push(feedLayerRef.current);
        if (highlightLayerRef.current) {
          layers.push(highlightLayerRef.current);
        }

        const request = ++hoverRequest;

        if (!layers.length) {
          setOverMarker(false);
          return;
        }

        view.hitTest(event, { include: layers }).then((response) => {
          // Kým hitTest bežal, myš už mohla byť inde - vtedy jeho výsledok
          // zahodíme, aby kurzor neprebliklo staršie zistenie.
          if (request !== hoverRequest) return;
          setOverMarker(
            response.results.some((result) => result.type === 'graphic'),
          );
        });
      });
    },
    [onSearchLocationChange, onViewReady, restoreViewBeforeDetail],
  );

  const oldCenter = React.useRef<__esri.Point>(null);
  const handleMoveTimeout = React.useRef<number>(null);

  const handleMove = React.useCallback(
    (viewport: MapViewport) => {
      if (!oldCenter.current) {
        oldCenter.current = viewport.center.clone();
      }

      // only update viewport state after a short inactivity
      clearTimeout(handleMoveTimeout.current);
      const settle = async () => {
        /**
         * Rozhoduje sa nad mapou, ktorá **stojí**, a nad jej polohou v tej
         * chvíli - nie nad udalosťou, ktorá časovač spustila. Komponent mapy
         * nehlási počas letu každý snímok, takže 300 ms po poslednej udalosti
         * mohol let (350 ms) ešte bežať: pri návrate z detailu tak táto
         * kontrola videla mapu ešte priblíženú pri podnete, poslala feed
         * na jeho okolie a zoznam skočil navrch, hoci mapa o chvíľu pristála
         * na pôvodnom mieste (moj-presov#24, odmerané výpisom v konzole).
         */
        const liveView = mapRef.current?.view;
        if (liveView && !liveView.stationary) {
          handleMoveTimeout.current = window.setTimeout(settle, 100);
          return;
        }
        const center = liveView?.center ?? viewport.center;
        const extent = liveView?.extent ?? viewport.extent;

        // Let do detailu je krok appky: zoznam pod detailom sa podľa neho
        // nedoťahuje ani nezoraďuje, inak by sa po zatvorení vrátil iný
        // zoznam, než z ktorého sa detail otváral, a pozícia v ňom by
        // nesedela (moj-presov#24). Za posunom mapy používateľom počas
        // detailu sa feed doťahuje (`movedInDetail`).
        if (viewBeforeDetail.current && !movedInDetail.current) return;

        onMapCenterChange?.({
          lng: center.longitude,
          lat: center.latitude,
        });

        const [projectOperator, distanceOperator] = await Promise.all([
          import('@arcgis/core/geometry/operators/projectOperator'),
          import('@arcgis/core/geometry/operators/distanceOperator'),
        ]);

        if (!projectOperator.isLoaded()) {
          await projectOperator.load();
        }

        const movedDistance = distanceOperator.execute(
          projectOperator.execute(oldCenter.current, center.spatialReference),
          center,
          { unit: 'kilometers' },
        );

        // Calculate radius dynamically
        let calculatedRadius = 10; // fallback
        if (extent) {
          try {
            const { default: Point } =
              await import('@arcgis/core/geometry/Point');
            const topLeftCorner = new Point({
              x: extent.xmax,
              y: extent.ymax,
              spatialReference: extent.spatialReference,
            });

            calculatedRadius = distanceOperator.execute(center, topLeftCorner, {
              unit: 'kilometers',
            });
          } catch (e) {
            console.error('Failed to calculate map radius:', e);
          }
        }

        // only update the search location if the user has moved significantly
        if (movedDistance > calculatedRadius / 2) {
          oldCenter.current = center.clone();
          onSearchLocationChange?.(`${center.longitude},${center.latitude}`);
        }
      };
      handleMoveTimeout.current = window.setTimeout(settle, 300);
    },
    [onMapCenterChange, onSearchLocationChange],
  );

  const mapSearch = (
    <MapSearch
      view={view}
      location={searchLocation}
      onSelectPlace={(coordinates, label) => {
        // Výber miesta je presun používateľa, aj keď ho robí `goTo`.
        if (viewBeforeDetail.current) movedInDetail.current = true;
        mapRef.current
          ?.goTo({ center: coordinates, zoom: 16 }, { duration: 350 })
          .catch(() => undefined);
        onSearchLocationChange?.(`${coordinates[0]},${coordinates[1]}`);
        // Nájdené miesto dostane značku s popisom. Bez nej sa mapa presunula
        // a nebolo na nej poznať, kam - popup geokódera to hovoril, ale bol to
        // popup ArcGISu bez našej témy, tak sme ho vypli.
        setSearchResult({ coordinates, label });
      }}
      onSelectReport={(reportId, coordinates) => {
        // Rovnaké priblíženie, aké dá detail sám: nájdený podnet ešte nemusí
        // byť v načítanom feede, takže sa mapa presúva podľa súradníc
        // z hľadania a detail už len dosadne na to isté miesto.
        if (coordinates) {
          mapRef.current
            ?.goTo(
              { center: coordinates, zoom: DETAIL_ZOOM },
              { duration: 350 },
            )
            .catch(() => undefined);
          onSearchLocationChange?.(`${coordinates[0]},${coordinates[1]}`);
        }
        onSearchReport?.(reportId);
      }}
    />
  );

  return (
    <Box
      sx={{
        width: 1,
        height: 1,
        position: 'relative',
      }}
    >
      <Stack
        sx={{
          alignItems: 'center',
          justifyContent: 'center',
          position: 'absolute',
          // Hore v strede stojí hľadanie, takže načítavanie ide pod neho.
          top: isLoading ? (isMd ? 70 : 60) : -100,
          opacity: isLoading ? 1 : 0,
          left: '50%',
          transform: 'translate(-50%, 0)',
          zIndex: 1,
          background: (theme) => theme.palette.background.paper,
          p: {
            xs: 1.2,
            md: 1.4,
          },
          borderRadius: (theme) =>
            theme.typography.pxToRem(theme.shape.borderRadius as number),
          border: (theme) =>
            `${theme.typography.pxToRem(1)} solid ${theme.palette.divider}`,
          transition: 'all 0.3s ease',
        }}
      >
        <CircularProgress size={!isMd ? 24 : 32} />
      </Stack>
      <GeoMap
        ref={mapRef}
        onReady={handleReady}
        onMove={handleMove}
        loading={isMapLoading}
        // Vlastný panel namiesto widgetu ArcGISu: v jednom zozname ponúka
        // miesta, podnety aj plus kód.
        //
        // Na mobile zostáva v slote mapy: je na plnú šírku, takže sa mu
        // ovládanie priblíženia v tom istom stohu zoradí pod neho. Na desktope
        // stojí v strede horného okraja (prekryv nižšie) a v ľavom hornom rohu
        // tak zostane samo ovládanie mapy.
        searchSlot={isMd ? undefined : mapSearch}
        locationIndicator
        onZoom={onZoom}
        initialLocation={
          initialCenter ? [initialCenter.lng, initialCenter.lat] : undefined
        }
        initialZoom={initialZoom}
        allowSingleFingerPan
      />

      {/* Hľadanie nad mapou a vedľa neho tlačidlo nového podnetu. Na desktope
          je to prekryv v strede horného okraja, nie uzol v slote mapy: slot má
          len rohy, a v ľavom hornom stojí ovládanie mapy. Stred sa počíta
          z voľnej plochy, teda bez šírky plávajúceho panela
          (`--feed-panel-width`) - inak by na užších obrazovkách ponuka
          výsledkov skončila pod ním. */}
      {isMd ? (
        <Stack
          direction='row'
          sx={{
            position: 'absolute',
            top: 20,
            left: 'calc((100% - var(--feed-panel-width, 0px)) / 2)',
            transform: 'translateX(-50%)',
            // Nie `center`: ponuka výsledkov visí v uzle hľadania, takže by
            // riadok pri písaní narástol a tlačidlo by sa posúvalo dole.
            // Obe majú výšku 38, takže od vrchu sedia bez centrovania.
            alignItems: 'flex-start',
            gap: 1.5,
            maxWidth: 'calc(100% - 40px)',
            // Widgety ArcGISu idú so `zIndex` do stoviek až tisícov, takže
            // prekryvy mapy musia ísť s nimi. Mapa je vlastný stacking context,
            // takže sa tie tisíce porovnávajú len medzi sebou. Hľadanie je
            // v tomto stohu najvyššie: jeho ponuka výsledkov musí ležať nad
            // čiarou k značke, nie pod ňou.
            zIndex: 1000,
          }}
        >
          {mapSearch}
          {searchAction}
        </Stack>
      ) : null}

      {/* Až s nakreslenou mapou: pred pohľadom nemá mapa `padding` a pin by
          stál inde, než kam ukazuje stred; pred dlaždicami a pod prekryvom
          načítavania by visel nad prázdnom. */}
      {showCenterPin && view && isMapDrawn && !isMapLoading ? (
        <MapCenterPin
          view={view}
          right={paddingRight}
          bottom={paddingBottom}
          loading={isLoading}
        />
      ) : null}

      {/* Čiarkovaná čiara od panela k značke. Kreslí ju mapa, lebo prepočítať
          polohu značky na obrazovku (`view.toScreen`) vie len ten, kto má
          pohľad. Kliknutia neberie - je to čiara, nie ovládací prvok. */}
      {isMd && selectedItem && (
        <Box
          component='svg'
          ref={connectorRef}
          sx={(theme) => ({
            position: 'absolute',
            inset: 0,
            width: 1,
            height: 1,
            color: theme.palette.primary.main,
            // Čiara na značku za hranou mapy z výrezu vychádza - `overflow`
            // ju odstrihne presne na hrane. Prehliadače to na koreňovom `svg`
            // robia aj samy, ale stojí na tom vzhľad, tak je to napísané.
            overflow: 'hidden',
            // Kým čiara nemá spočítaný tvar, nie je čo ukázať.
            visibility: 'hidden',
            pointerEvents: 'none',
            // Pod hľadaním (1000), nad mapou. Šípku na konci čiara nemá -
            // k značke prichádza vždy sprava, takže je jasné, kam ukazuje,
            // a hrot má na druhom konci: biely výstupok panela.
            zIndex: 900,
          })}
        >
          <path
            ref={connectorPathRef}
            fill='none'
            stroke='currentColor'
            strokeWidth={2}
            strokeDasharray='6 5'
            strokeLinecap='round'
          />
        </Box>
      )}
    </Box>
  );
};

export default FeedsMap;
